import { getPositionRowKey } from '@/lib/position-row-key'
import { OrderSide, type Position } from '@/types/generated/common/trading'

/**
 * Home's risk check: which open positions a newcomer should look at first. Leverage and the
 * distance to the venue's liquidation price are the two numbers that decide whether a normal
 * market move wipes a position out — the 100x-leverage trap — so they drive the flags here.
 * Pure, so the thresholds are tested (__tests__/home-risk.test.ts).
 */

export type RiskLevel = 'danger' | 'warning' | 'ok'

export type RiskReason = 'nearLiquidation' | 'highLeverage' | 'deepLoss'

export interface PositionRisk {
  key: string
  position: Position
  side: 'long' | 'short'
  /** Venue-reported leverage on the position; null when the venue doesn't report one. */
  leverage: number | null
  /**
   * How far the mark price may still move against the position before the venue's liquidation
   * price (0.032 = 3.2%); 0 when it is already past it, null when the venue reports none.
   */
  liquidationDistance: number | null
  unrealizedPnlUsd: number | null
  /** Return on committed capital, percent (-60 = -60%). */
  pnlPercent: number | null
  level: RiskLevel
  reasons: RiskReason[]
}

/** At or above this leverage a ~5% move against the position is enough to liquidate it. */
export const HIGH_LEVERAGE = 20
/** At or above this leverage a ~2% move is enough: flagged as danger outright. */
export const EXTREME_LEVERAGE = 50
/** Liquidation within this fraction of the mark price is worth a look. */
export const NEAR_LIQUIDATION = 0.1
/** Liquidation within this fraction of the mark price is one ordinary candle away. */
export const CRITICAL_LIQUIDATION = 0.03
/** A position down this much (percent of committed capital) is flagged. */
export const DEEP_LOSS_PERCENT = -50

const LEVEL_RANK: Record<RiskLevel, number> = { danger: 2, warning: 1, ok: 0 }

function toNumber(value: string | null | undefined): number | null {
  if (value == null || value.trim() === '') return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

function positionSide(position: Position): 'long' | 'short' {
  if (position.side === OrderSide.ORDER_SIDE_BUY) return 'long'
  if (position.side === OrderSide.ORDER_SIDE_SELL) return 'short'
  return (toNumber(position.quantity) ?? 0) < 0 ? 'short' : 'long'
}

export function liquidationDistance(
  side: 'long' | 'short',
  markPrice: number | null,
  liquidationPrice: number | null
): number | null {
  if (markPrice === null || markPrice <= 0 || liquidationPrice === null || liquidationPrice <= 0)
    return null
  const distance =
    side === 'long'
      ? (markPrice - liquidationPrice) / markPrice
      : (liquidationPrice - markPrice) / markPrice
  return Math.max(0, distance)
}

export function assessPositionRisk(position: Position): PositionRisk {
  const side = positionSide(position)
  const leverage = toNumber(position.currentLeverage)
  const markPrice = toNumber(position.markPrice)
  const distance = liquidationDistance(side, markPrice, toNumber(position.liquidationPrice))
  const pnlPercent = toNumber(position.pnlPercent)

  const reasons: RiskReason[] = []
  let level: RiskLevel = 'ok'
  const raise = (to: RiskLevel) => {
    if (LEVEL_RANK[to] > LEVEL_RANK[level]) level = to
  }

  if (distance !== null && distance <= NEAR_LIQUIDATION) {
    reasons.push('nearLiquidation')
    raise(distance <= CRITICAL_LIQUIDATION ? 'danger' : 'warning')
  }
  if (leverage !== null && leverage >= HIGH_LEVERAGE) {
    reasons.push('highLeverage')
    raise(leverage >= EXTREME_LEVERAGE ? 'danger' : 'warning')
  }
  if (pnlPercent !== null && pnlPercent <= DEEP_LOSS_PERCENT) {
    reasons.push('deepLoss')
    raise('warning')
  }

  return {
    key: getPositionRowKey(position.accountId, position.exchange, position.symbol),
    position,
    side,
    leverage,
    liquidationDistance: distance,
    unrealizedPnlUsd: toNumber(position.unrealizedPnlUsd),
    pnlPercent,
    level,
    reasons,
  }
}

export interface PortfolioRisk {
  /** Every open position, most at risk first (then closest to liquidation, then largest loss). */
  items: PositionRisk[]
  flaggedCount: number
  level: RiskLevel
}

export function assessPortfolioRisk(positions: readonly Position[]): PortfolioRisk {
  const items = positions
    .filter((position) => (toNumber(position.quantity) ?? 0) !== 0)
    .map(assessPositionRisk)
    .sort(
      (a, b) =>
        LEVEL_RANK[b.level] - LEVEL_RANK[a.level] ||
        (a.liquidationDistance ?? Infinity) - (b.liquidationDistance ?? Infinity) ||
        (a.unrealizedPnlUsd ?? 0) - (b.unrealizedPnlUsd ?? 0)
    )
  const flaggedCount = items.filter((item) => item.level !== 'ok').length
  const level = items[0]?.level ?? 'ok'
  return { items, flaggedCount, level }
}
